import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatCurrency, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Check, Crown, Loader2, CalendarClock, Receipt } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Plans() {
  const { company, user, refreshCompany } = useAuth();
  const [plans, setPlans] = useState([]);
  const [mySub, setMySub] = useState(null);
  const [openCheckout, setOpenCheckout] = useState(null);
  const [cycle, setCycle] = useState("MONTHLY");
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  const nav = useNavigate();

  const load = () => {
    api.get("/plans").then((r) => setPlans(r.data));
    api.get("/subscriptions/mine").then((r) => setMySub(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Handle payment return: poll transaction status
  useEffect(() => {
    const paid = params.get("paid");
    if (!paid) return;
    let tries = 0;
    const iv = setInterval(async () => {
      tries++;
      try {
        const { data } = await api.get("/transactions/mine");
        const txn = data.find((t) => t.transaction_ref === paid);
        if (txn?.status === "SUCCESS") {
          clearInterval(iv);
          toast.success(`✅ Paiement confirmé — plan ${txn.plan_id?.toUpperCase()} activé`);
          refreshCompany();
          load();
          nav("/app/subscription", { replace: true });
        } else if (txn?.status === "FAILED" || tries > 15) {
          clearInterval(iv);
          if (txn?.status === "FAILED") toast.error("Paiement refusé — abonnement non activé");
          else toast.warning("Paiement en cours de vérification. Consultez 'Mon abonnement'.");
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(iv);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("paid")]);

  const startCheckout = async (plan, chosenCycle) => {
    setLoading(true);
    try {
      const { data } = await api.post("/subscriptions/checkout", { plan_id: plan.id, billing_cycle: chosenCycle });
      if (data.payment_url) {
        toast.info("Redirection vers le paiement…");
        window.location.href = data.payment_url;
      } else {
        toast.error("Impossible d'initier le paiement");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec du paiement");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (plan) => {
    if (user?.role !== "createur") { toast.error("Seul le créateur peut souscrire"); return; }
    if (plan.price_monthly <= 0) return;
    setOpenCheckout(plan);
    setCycle("MONTHLY");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Abonnement</h1>
          <p className="text-sm text-slate-500">
            Plan actuel : <b>{company?.plan || "FREE"}</b>
            {mySub?.expiration_date && <> · Expire le {new Date(mySub.expiration_date).toLocaleDateString("fr-FR")}</>}
          </p>
        </div>
        <Button variant="outline" onClick={() => nav("/app/subscription")} data-testid="my-subscription-btn"><Receipt className="w-4 h-4 mr-1" /> Mon abonnement & factures</Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((p) => {
          const current = (company?.plan || "FREE").toUpperCase() === p.name.toUpperCase();
          const isFree = p.price_monthly <= 0;
          return (
            <Card key={p.id} data-testid={`plan-${p.name}`} className={`${p.highlight ? "border-orange-500 shadow-lg shadow-orange-500/10" : ""} ${current ? "ring-2 ring-orange-500" : ""}`}>
              <CardHeader className="pb-3">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{p.label}</div>
                <CardTitle className="font-display flex items-center gap-2">{p.name}{p.highlight && <Crown className="w-4 h-4 text-orange-500" />}</CardTitle>
                <div className="flex items-baseline gap-1 pt-2">
                  <span className="font-display font-bold text-3xl">{formatCurrency(p.price_monthly, p.currency)}</span>
                  <span className="text-xs text-slate-500">/mois</span>
                </div>
                <div className="text-xs text-slate-500">Annuel : {formatCurrency(p.price_annual, p.currency)}</div>
              </CardHeader>
              <CardContent className="space-y-3">
                <ul className="space-y-1.5 text-sm">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />{f}</li>
                  ))}
                </ul>
                <div className="text-xs text-slate-500">Max staff : {p.max_staff} · {(p.max_tickets_per_month || 0).toLocaleString("fr-FR")} billets/mois</div>
                <Button
                  disabled={current || (isFree && current)}
                  onClick={() => isFree ? null : openDialog(p)}
                  className={`btn-pill w-full ${current ? "" : "bg-orange-500 hover:bg-orange-600 text-white"}`}
                  data-testid={`subscribe-${p.name}`}
                >
                  {current ? "Plan actuel" : (isFree ? "Gratuit" : "Souscrire")}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!openCheckout} onOpenChange={(o) => !o && setOpenCheckout(null)}>
        <DialogContent data-testid="checkout-dialog">
          <DialogHeader><DialogTitle>Souscrire au plan {openCheckout?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Choisissez la période de facturation. Le montant sera calculé côté serveur et vous serez redirigé vers la page de paiement sécurisée.</p>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setCycle("MONTHLY")} className={`p-4 rounded-2xl border-2 text-left transition ${cycle === "MONTHLY" ? "border-orange-500 bg-orange-50 dark:bg-orange-500/10" : "border-slate-200 dark:border-slate-700"}`} data-testid="cycle-monthly">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Mensuel</div>
                <div className="font-display font-bold text-xl mt-1">{openCheckout && formatCurrency(openCheckout.price_monthly, openCheckout.currency)}</div>
                <div className="text-xs text-slate-500 mt-1">/ mois — 30 jours</div>
              </button>
              <button onClick={() => setCycle("YEARLY")} className={`p-4 rounded-2xl border-2 text-left transition ${cycle === "YEARLY" ? "border-orange-500 bg-orange-50 dark:bg-orange-500/10" : "border-slate-200 dark:border-slate-700"}`} data-testid="cycle-yearly">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Annuel <span className="text-emerald-600">-17%</span></div>
                <div className="font-display font-bold text-xl mt-1">{openCheckout && formatCurrency(openCheckout.price_annual, openCheckout.currency)}</div>
                <div className="text-xs text-slate-500 mt-1">/ an — 365 jours</div>
              </button>
            </div>
            <div className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-900 p-3 rounded-lg flex gap-2 items-start">
              <CalendarClock className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{mySub?.expiration_date ? `Votre abonnement actuel expire le ${new Date(mySub.expiration_date).toLocaleDateString("fr-FR")}. La nouvelle période sera ajoutée à cette date (jours restants conservés).` : "L'activation démarre dès confirmation du paiement par le fournisseur."}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCheckout(null)}>Annuler</Button>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={() => startCheckout(openCheckout, cycle)} disabled={loading} data-testid="checkout-submit">
              {loading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Redirection…</> : "Payer maintenant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
